import { marked } from "marked";

marked.setOptions({
  gfm: true,
  breaks: true,
});

const ALLOWED_TAGS = new Set([
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "P",
  "BR",
  "HR",
  "UL",
  "OL",
  "LI",
  "TABLE",
  "THEAD",
  "TBODY",
  "TFOOT",
  "TR",
  "TH",
  "TD",
  "STRONG",
  "EM",
  "B",
  "I",
  "U",
  "SUP",
  "SUB",
  "A",
  "BLOCKQUOTE",
  "PRE",
  "CODE",
  "SPAN",
  "DIV",
]);

const ALLOWED_ATTRS: Record<string, Set<string>> = {
  A: new Set(["href", "title", "target", "rel"]),
  TH: new Set(["colspan", "rowspan", "align"]),
  TD: new Set(["colspan", "rowspan", "align"]),
  OL: new Set(["start"]),
};

const unwrapTexSymbols = (markdown: string): string =>
  markdown
    .replace(/\$\\ge\$/g, "≥")
    .replace(/\$\\le\$/g, "≤")
    .replace(/\$\\geq\$/g, "≥")
    .replace(/\$\\leq\$/g, "≤")
    .replace(/\$≥\$/g, "≥")
    .replace(/\$≤\$/g, "≤");

const isSafeHref = (href: string): boolean =>
  /^(https?:|mailto:|#)/i.test(href.trim());

const REMOVE_TAGS = new Set([
  "SCRIPT",
  "STYLE",
  "IFRAME",
  "OBJECT",
  "EMBED",
  "LINK",
  "META",
  "FORM",
  "INPUT",
  "BUTTON",
]);

export const sanitizeGuidelineHtml = (html: string): string => {
  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return html;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div id="root">${html}</div>`, "text/html");
  const root = doc.getElementById("root");
  if (!root) return "";

  const walk = (node: Node) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === Node.COMMENT_NODE) {
        child.parentNode?.removeChild(child);
        return;
      }

      if (child.nodeType !== Node.ELEMENT_NODE) return;

      const element = child as HTMLElement;
      const tag = element.tagName;

      if (REMOVE_TAGS.has(tag)) {
        element.remove();
        return;
      }

      walk(element);

      if (!ALLOWED_TAGS.has(tag)) {
        const fragment = doc.createDocumentFragment();
        while (element.firstChild) {
          fragment.appendChild(element.firstChild);
        }
        element.replaceWith(fragment);
        return;
      }

      Array.from(element.attributes).forEach((attr) => {
        const allowed = ALLOWED_ATTRS[tag];
        const name = attr.name.toLowerCase();
        if (name.startsWith("on") || !allowed || !allowed.has(name)) {
          element.removeAttribute(attr.name);
          return;
        }
        if (name === "href" && !isSafeHref(attr.value)) {
          element.removeAttribute(attr.name);
        }
      });

      if (tag === "A") {
        element.setAttribute("rel", "noopener noreferrer");
        if (!element.getAttribute("target")) {
          element.setAttribute("target", "_blank");
        }
      }
    });
  };

  walk(root);
  return root.innerHTML;
};

export const renderGuidelineMarkdown = (markdown: string): string => {
  if (!markdown?.trim()) return "";
  const prepared = unwrapTexSymbols(markdown);
  const html = marked.parse(prepared, { async: false }) as string;
  return sanitizeGuidelineHtml(html);
};
